import React from 'react';

// Utils
import {
  SCHEMA_TYPE_ENUM,
  SCHEMA_TYPE_LONG,
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_TEXT,
  SCHEMA_TYPE_YOUTUBE,
} from '../../util/types';
import {
  isFieldForCategory,
  pickCategoryFields,
  pickCustomFieldProps,
} from '../../util/fieldHelpers.js';

import SectionDetailsMaybe from './SectionDetailsMaybe';
import SectionMultiEnumMaybe from './SectionMultiEnumMaybe';
import SectionTextMaybe from './SectionTextMaybe';
import SectionYoutubeVideoMaybe from './SectionYoutubeVideoMaybe';
import { FormattedMessage } from 'react-intl';
import ShowMoreChildren from './ShowMoreChildren.js';
import SectionEnumMaybe from './SectionEnumMaybe.js';
import { SectionEnginesSimple } from './SectionEngines/SectionEngines.js';
import SectionMooring from './SectionMooring/SectionMooring.js';
import AdditionalInfo from './AdditionalInfo/AdditionalInfo.js';
import SectionDescription from './SectionDescription.js';

import css from './ListingPage.module.css';

const meterFields = ['length', 'beam', 'loa', 'draught', 'draughtKeelup', 'clearance', 'headroom'];
const kilogramFields = ['displacement', 'curbWeight', 'payload'];
export const allowedFields = [
  'saleClass',
  'condition',
  'hullMaterial',
  'loa',
  'beam',
  'passengerCapacity',
  'draught',
  'clearance',
  'equipment',
  'coversAndTarpaulins',
  'galley',
  'onBoardInstruments',
  'technicalEquipment',
];

/**
 * Renders custom listing fields.
 * - SectionDetailsMaybe is used if schemaType is 'enum', 'long', or 'boolean'
 * - SectionMultiEnumMaybe is used if schemaType is 'multi-enum'
 * - SectionTextMaybe is used if schemaType is 'text'
 *
 * @param {*} props include publicData, metadata, listingFieldConfigs, categoryConfiguration
 * @returns React.Fragment containing aforementioned components
 */
const CustomListingFields = props => {
  const {
    publicData,
    metadata,
    listingFieldConfigs,
    categoryConfiguration,
    extraFieldConfigs = [],
    shouldGroup,
  } = props;

  const { key: categoryPrefix, categories: listingCategoriesConfig } = categoryConfiguration;
  const categoriesObj = pickCategoryFields(publicData, categoryPrefix, 1, listingCategoriesConfig);
  const currentCategories = Object.values(categoriesObj);

  const isFieldForSelectedCategories = fieldConfig => {
    const isTargetCategory = isFieldForCategory(currentCategories, fieldConfig);
    return isTargetCategory;
  };
  const propsForCustomFields =
    pickCustomFieldProps(
      publicData,
      metadata,
      listingFieldConfigs,
      'listingType',
      isFieldForSelectedCategories
    ).filter(f => allowedFields.includes(f.key)) || [];

  const { condition, engineModel, boatEngines, boatMooring, boatMooringAvailable, saleClass } =
    publicData || {};

  const renderFields = fields =>
    fields.map(customFieldProps => {
      const { schemaType, key, dontRender, ...fieldProps } = customFieldProps;
      return dontRender ? null : schemaType === SCHEMA_TYPE_MULTI_ENUM ? (
        <SectionMultiEnumMaybe key={key} {...fieldProps} showUnselectedOptions={false} />
      ) : schemaType === SCHEMA_TYPE_TEXT ? (
        <SectionTextMaybe key={key} {...fieldProps} />
      ) : schemaType === SCHEMA_TYPE_YOUTUBE ? (
        <SectionYoutubeVideoMaybe key={key} {...fieldProps} />
      ) : schemaType === SCHEMA_TYPE_LONG ? (
        <SectionTextMaybe
          key={key}
          {...fieldProps}
          heading={
            [...meterFields, ...kilogramFields].includes(key)
              ? `${fieldProps.heading}.noUnit`
              : fieldProps.heading
          }
          text={
            meterFields.includes(key)
              ? `${fieldProps.value} meter`
              : kilogramFields.includes(key)
              ? `${fieldProps.value} kg`
              : fieldProps.value
          }
        />
      ) : schemaType === SCHEMA_TYPE_ENUM ? (
        <SectionEnumMaybe
          key={key}
          {...fieldProps}
          heading={
            ['hullMaterial'].includes(key) ? `${fieldProps.heading}.noUnit` : fieldProps.heading
          }
        />
      ) : null;
    });

  const equipmentFields = shouldGroup
    ? propsForCustomFields
        .filter(f => f.schemaType === SCHEMA_TYPE_MULTI_ENUM)
        ?.map(field => ({ ...field, dontRender: false }))
    : null;

  const numberFields = shouldGroup
    ? propsForCustomFields.filter(f => f.schemaType === SCHEMA_TYPE_LONG)
    : null;

  const enumFields = shouldGroup
    ? propsForCustomFields.filter(f => f?.schemaType === SCHEMA_TYPE_ENUM)
    : null;
  const [descriptionConfig] = extraFieldConfigs;

  const hasEquipment =
    equipmentFields
      ?.map(field => field?.selectedOptions?.length)
      ?.reduce((acc, val) => acc + val, 0) > 0;

  return (
    <>
      <SectionDetailsMaybe {...props} isFieldForCategory={isFieldForSelectedCategories} />
      {!shouldGroup ? (
        renderFields(propsForCustomFields)
      ) : (
        <>
          <ShowMoreChildren
            lines={8}
            anchorClass={css.showMoreLessClickable}
            className={css.sectionDetails}
          >
            {saleClass && (
              <SectionTextMaybe
                text={
                  <>
                    <FormattedMessage id={`General.listing.saleClass.${saleClass}`} />
                    {condition && (
                      <>
                        {' / '}
                        <FormattedMessage id={`General.listing.condition.${condition}`} />
                      </>
                    )}
                  </>
                }
                heading="EditListingLocationForm.saleClassLabel"
              />
            )}
            {engineModel && (
              <SectionTextMaybe text={engineModel} heading="EditListingDetailsForm.engineModel" />
            )}

            {renderFields(numberFields).filter(Boolean)}
            {renderFields(enumFields).filter(Boolean)}
          </ShowMoreChildren>
          <SectionEnginesSimple engines={boatEngines} />
          <AdditionalInfo listingInfo={publicData} />

          {/* Description */}
          <SectionDescription text={descriptionConfig.text} />

          <SectionMooring mooring={boatMooringAvailable && boatMooring} />
        </>
      )}
    </>
  );
};

export default CustomListingFields;
